import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Share, X, PlusSquare, MoreVertical } from "lucide-react";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); setShowGuide(false); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Once installed there is nothing to install.
  if (installed) return null;

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferred(null);
    } else {
      // No native prompt available (iOS Safari, desktop Safari/Firefox, or
      // Chrome before the event fires) — always show manual guidance.
      setShowGuide(true);
    }
  };

  return (
    <>
      <Button
        onClick={install}
        data-testid="install-app-btn"
        className="rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white"
      >
        <Download className="w-4 h-4 mr-1.5" /> Install App
      </Button>

      {showGuide && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 px-4" data-testid="install-guide" onClick={() => setShowGuide(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <h3 className="font-heading font-bold text-lg text-brand-ink">Install My Property</h3>
              <button onClick={() => setShowGuide(false)} data-testid="install-guide-close" className="text-brand-muted hover:text-brand-ink"><X className="w-5 h-5" /></button>
            </div>
            {isIOS() ? (
              <>
                <p className="text-sm text-brand-muted mt-3">To add this app to your iPhone/iPad home screen:</p>
                <ol className="mt-4 space-y-3 text-sm text-brand-ink">
                  <li className="flex items-center gap-3"><Share className="w-5 h-5 text-brand-emerald shrink-0" /> Tap the <b>Share</b> button in Safari's toolbar</li>
                  <li className="flex items-center gap-3"><PlusSquare className="w-5 h-5 text-brand-emerald shrink-0" /> Choose <b>Add to Home Screen</b></li>
                </ol>
              </>
            ) : (
              <>
                <p className="text-sm text-brand-muted mt-3">To install this app on your device:</p>
                <ol className="mt-4 space-y-3 text-sm text-brand-ink">
                  <li className="flex items-center gap-3"><MoreVertical className="w-5 h-5 text-brand-emerald shrink-0" /> Open your browser <b>menu</b> (⋮ or ⋯)</li>
                  <li className="flex items-center gap-3"><Download className="w-5 h-5 text-brand-emerald shrink-0" /> Choose <b>Install app</b> or <b>Add to Home screen</b></li>
                </ol>
                <p className="text-xs text-brand-muted mt-4">Tip: on desktop Chrome/Edge, look for the install icon at the right end of the address bar.</p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
